import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Logo from '@/components/Logo';
import {
  Globe, TrendUp, CheckCircle, WarningCircle, XCircle, Sparkle,
  ArrowRight, ArrowsClockwise, Trash, Storefront, Gauge, Wrench,
} from '@phosphor-icons/react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLOR = {
  pending: 'text-[#71717A]',
  analyzing: 'text-[#F59E0B]',
  completed: 'text-[#16A34A]',
  failed: 'text-[#DC2626]',
};

const SEVERITY_COLOR = {
  high: 'border-l-[#DC2626] bg-red-50',
  medium: 'border-l-[#F59E0B] bg-amber-50',
  low: 'border-l-[#71717A] bg-[#F4F4F5]',
};

const StoresPage = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [companies, setCompanies] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newUrl, setNewUrl] = useState('');
  const [newCompanyId, setNewCompanyId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };

  const load = async () => {
    try {
      const [cRes, sRes] = await Promise.all([
        axios.get(`${API_URL}/companies`, auth),
        axios.get(`${API_URL}/stores`, auth),
      ]);
      setCompanies(cRes.data);
      setStores(sRes.data);
      if (cRes.data.length > 0 && !newCompanyId) setNewCompanyId(String(cRes.data[0].id));
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    document.title = language === 'tr' ? 'Mağaza Analizleri — GrowthRIQ' : 'Store Analyses — GrowthRIQ';
    load();
  }, []);

  // Poll if any store is analyzing (with safety timeout to prevent infinite polling)
  useEffect(() => {
    const analyzing = stores.some(s => s.status === 'pending' || s.status === 'analyzing');
    if (!analyzing) return;
    const startedAt = Date.now();
    const MAX_POLL_MS = 3 * 60 * 1000; // 3 minutes
    const id = setInterval(() => {
      if (Date.now() - startedAt > MAX_POLL_MS) {
        clearInterval(id);
        toast.info(language === 'tr'
          ? 'Analiz beklenenden uzun sürüyor. Gerekirse "Yenile" ile tekrar deneyin.'
          : 'Analysis is taking longer than expected. Click "Refresh" to retry.');
        return;
      }
      load();
    }, 4000);
    return () => clearInterval(id);
  }, [stores]);

  // Load analysis when store selected & completed
  useEffect(() => {
    if (!selectedStoreId) { setAnalysis(null); return; }
    const store = stores.find(s => s.id === selectedStoreId);
    if (!store || store.status !== 'completed') { setAnalysis(null); return; }
    axios.get(`${API_URL}/stores/${selectedStoreId}/analysis`, auth)
      .then(r => setAnalysis(r.data))
      .catch(() => setAnalysis(null));
  }, [selectedStoreId, stores]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newUrl || !newCompanyId) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/stores`,
        { company_id: parseInt(newCompanyId), url: newUrl },
        auth
      );
      toast.success(language === 'tr' ? 'Mağaza eklendi — analiz başlatıldı' : 'Store added — analysis started');
      setNewUrl('');
      setStores([res.data, ...stores]);
      setSelectedStoreId(res.data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSubmitting(false); }
  };

  const handleReanalyze = async (storeId) => {
    try {
      await axios.post(`${API_URL}/stores/${storeId}/reanalyze`, {}, auth);
      toast.success(language === 'tr' ? 'Yeniden analiz başlatıldı' : 'Re-analysis started');
      load();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (storeId) => {
    if (!window.confirm(language === 'tr' ? 'Silinsin mi?' : 'Delete?')) return;
    try {
      await axios.delete(`${API_URL}/stores/${storeId}`, auth);
      toast.success(language === 'tr' ? 'Silindi' : 'Deleted');
      if (selectedStoreId === storeId) setSelectedStoreId(null);
      load();
    } catch { toast.error('Failed'); }
  };

  const statusLabel = (s) => {
    const m = {
      tr: { pending: 'Bekliyor', analyzing: 'Analiz ediliyor…', completed: 'Tamamlandı', failed: 'Hata' },
      en: { pending: 'Pending', analyzing: 'Analyzing…', completed: 'Completed', failed: 'Failed' },
    };
    return m[language][s] || s;
  };

  return (
    <div className="min-h-screen bg-white">
      <nav className="border-b border-[#E4E4E7] sticky top-0 bg-white/95 backdrop-blur z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <Logo variant="light" size="md" />
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Link to="/dashboard" className="text-[#52525B] hover:text-[#09090B] hidden sm:inline" data-testid="back-dashboard">
              {language === 'tr' ? 'Panel' : 'Dashboard'}
            </Link>
            <span className="text-sm text-[#52525B] hidden md:inline">{user?.email}</span>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-2">
          <Storefront size={32} weight="bold" className="text-[#0F1B36]" />
          <h1 className="heading-2 text-[#09090B]" data-testid="stores-title">
            {language === 'tr' ? 'Mağaza Analizleri' : 'Store Analyses'}
          </h1>
        </div>
        <p className="text-[#52525B] mb-8 max-w-2xl">
          {language === 'tr'
            ? 'E-ticaret siteniz veya Trendyol/Hepsiburada/Amazon mağazanızın URL\'sini girin. Sistem otomatik olarak SEO, hız, tech stack ve içerik analizi yapar; AI ile önceliklendirilmiş aksiyon planı sunar.'
            : 'Paste your e-commerce site or marketplace store URL. We auto-analyze SEO, speed, tech stack, and content, then AI gives you a prioritized action plan.'}
        </p>

        {/* Add form */}
        <form onSubmit={handleAdd} className="border border-[#E4E4E7] p-6 mb-10 bg-[#F4F4F5]" data-testid="store-add-form">
          <h2 className="font-semibold text-[#09090B] mb-4">
            {language === 'tr' ? 'Yeni Mağaza Ekle' : 'Add a Store'}
          </h2>
          <div className="grid md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-4">
              <Label>{language === 'tr' ? 'Şirket' : 'Company'}</Label>
              <Select value={newCompanyId} onValueChange={setNewCompanyId}>
                <SelectTrigger className="rounded-none" data-testid="store-company-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {companies.map(c => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-6">
              <Label>URL</Label>
              <Input
                type="text"
                placeholder="https://mystore.com veya https://trendyol.com/magaza/..."
                value={newUrl}
                onChange={e => setNewUrl(e.target.value)}
                className="rounded-none h-10"
                data-testid="store-url-input"
                required
              />
            </div>
            <div className="md:col-span-2">
              <Button type="submit" disabled={submitting || !companies.length}
                className="w-full rounded-none bg-[#0F1B36] hover:bg-[#00227A] text-white h-10"
                data-testid="store-submit-btn">
                {submitting ? '…' : (language === 'tr' ? 'Analiz Et' : 'Analyze')}
              </Button>
            </div>
          </div>
          {!companies.length && (
            <p className="text-sm text-[#DC2626] mt-3">
              {language === 'tr' ? 'Önce bir şirket oluşturmalısınız. ' : 'You must create a company first. '}
              <Link to="/companies" className="underline">{language === 'tr' ? 'Şirketler' : 'Companies'}</Link>
            </p>
          )}
        </form>

        {/* Stores list + detail */}
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Left: list */}
          <div className="lg:col-span-4">
            <h3 className="label-text text-[#71717A] mb-3">{language === 'tr' ? 'MAĞAZALARIM' : 'MY STORES'}</h3>
            {loading ? (
              <div className="text-[#71717A]" data-testid="stores-loading">Loading…</div>
            ) : stores.length === 0 ? (
              <div className="border border-dashed border-[#E4E4E7] p-8 text-center text-[#71717A]" data-testid="stores-empty">
                {language === 'tr' ? 'Henüz mağaza yok.' : 'No stores yet.'}
              </div>
            ) : (
              <div className="space-y-2" data-testid="stores-list">
                {stores.map(s => (
                  <button
                    key={s.id}
                    onClick={() => setSelectedStoreId(s.id)}
                    className={`w-full text-left p-4 border transition-colors ${selectedStoreId === s.id ? 'border-[#0F1B36] bg-[#F4F4F5]' : 'border-[#E4E4E7] hover:border-[#71717A]'}`}
                    data-testid={`store-item-${s.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <Globe size={18} weight="bold" className="text-[#71717A] flex-shrink-0 mt-1" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-[#09090B] truncate">{s.url.replace(/^https?:\/\//, '')}</div>
                        <div className="flex items-center gap-2 mt-1 text-xs">
                          <span className="uppercase tracking-wide text-[#71717A]">{s.store_type.replace('_', ' ')}</span>
                          <span>·</span>
                          <span className={STATUS_COLOR[s.status]}>{statusLabel(s.status)}</span>
                          {s.platform_detected && s.platform_detected !== 'unknown' && (
                            <><span>·</span><span className="text-[#0F1B36]">{s.platform_detected}</span></>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: detail */}
          <div className="lg:col-span-8">
            {!selectedStoreId && (
              <div className="border border-dashed border-[#E4E4E7] p-12 text-center text-[#71717A]">
                {language === 'tr' ? 'Solda bir mağaza seçin.' : 'Select a store on the left.'}
              </div>
            )}

            {selectedStoreId && (() => {
              const store = stores.find(s => s.id === selectedStoreId);
              if (!store) return null;

              if (store.status === 'pending' || store.status === 'analyzing') {
                return (
                  <div className="border border-[#E4E4E7] p-12 text-center" data-testid="analysis-inprogress">
                    <Gauge size={48} weight="bold" className="text-[#F59E0B] mx-auto mb-4 animate-pulse" />
                    <h3 className="heading-4 text-[#09090B] mb-2">
                      {language === 'tr' ? 'Analiz devam ediyor…' : 'Analysis in progress…'}
                    </h3>
                    <p className="text-[#52525B]">
                      {language === 'tr'
                        ? 'SEO, hız, tech stack ve AI analizi yaklaşık 30-60 saniye sürer. Bu sayfa otomatik güncellenir.'
                        : 'SEO, speed, tech, and AI synthesis take about 30-60 seconds. This page auto-refreshes.'}
                    </p>
                  </div>
                );
              }

              if (store.status === 'failed') {
                return (
                  <div className="border border-[#DC2626] p-8" data-testid="analysis-failed">
                    <XCircle size={32} weight="bold" className="text-[#DC2626] mb-3" />
                    <h3 className="font-semibold text-[#09090B] mb-1">{language === 'tr' ? 'Analiz başarısız' : 'Analysis failed'}</h3>
                    <p className="text-[#52525B] text-sm mb-4">{store.error_message || 'Unknown error'}</p>
                    <Button onClick={() => handleReanalyze(store.id)} className="rounded-none bg-[#0F1B36] text-white">
                      {language === 'tr' ? 'Tekrar dene' : 'Retry'}
                    </Button>
                  </div>
                );
              }

              if (!analysis) return <div className="text-[#71717A]" data-testid="analysis-loading">Loading…</div>;

              return (
                <div data-testid="analysis-detail">
                  {/* Header */}
                  <div className="border border-[#E4E4E7] p-6 mb-6">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div>
                        <a href={store.url} target="_blank" rel="noreferrer" className="text-[#0F1B36] font-semibold hover:underline text-lg">
                          {store.url.replace(/^https?:\/\//, '')}
                        </a>
                        <div className="text-sm text-[#71717A] mt-1">
                          {store.platform_detected || 'unknown platform'} · {new Date(analysis.created_at).toLocaleString(language)}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleReanalyze(store.id)} className="rounded-none" data-testid="reanalyze-btn">
                          <ArrowsClockwise size={14} weight="bold" className="mr-1" />
                          {language === 'tr' ? 'Yenile' : 'Refresh'}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDelete(store.id)} className="rounded-none text-[#DC2626]" data-testid="delete-btn">
                          <Trash size={14} weight="bold" />
                        </Button>
                      </div>
                    </div>

                    <div className="text-6xl font-bold text-[#0F1B36]" data-testid="overall-score">
                      {analysis.overall_score}
                      <span className="text-2xl text-[#71717A]">/100</span>
                    </div>

                    <div className="grid grid-cols-5 gap-3 mt-6" data-testid="category-scores">
                      {Object.entries(analysis.category_scores || {}).map(([key, val]) => (
                        <div key={key} className="text-center">
                          <div className="text-2xl font-bold text-[#09090B]">{val}</div>
                          <div className="text-xs text-[#71717A] uppercase tracking-wide mt-1">{key}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Summary */}
                  {(language === 'tr' ? analysis.summary_tr : analysis.summary_en) && (
                    <div className="border-l-4 border-[#0F1B36] bg-[#F4F4F5] p-4 mb-6" data-testid="summary-block">
                      <p className="text-[#09090B] leading-relaxed">
                        {language === 'tr' ? analysis.summary_tr : analysis.summary_en}
                      </p>
                    </div>
                  )}

                  {/* Marketplace-specific signals */}
                  {analysis.content_data?.marketplace && (
                    <div className="border border-[#E4E4E7] p-4 mb-6" data-testid="marketplace-block">
                      <h3 className="font-semibold text-[#09090B] mb-3 flex items-center gap-2">
                        <Storefront size={18} weight="bold" className="text-[#0F1B36]" />
                        {language === 'tr' ? 'Mağaza Sinyalleri' : 'Marketplace Signals'}
                        <span className="text-xs text-[#71717A] uppercase ml-1">
                          ({analysis.content_data.marketplace.marketplace})
                        </span>
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          ['page_title', language === 'tr' ? 'Sayfa Başlığı' : 'Page Title'],
                          ['product_cards_on_page', language === 'tr' ? 'Sayfa Ürünü' : 'Products Shown'],
                          ['product_count_estimated', language === 'tr' ? 'Tahmini Toplam' : 'Estimated Total'],
                          ['rating', language === 'tr' ? 'Puan' : 'Rating'],
                          ['review_count', language === 'tr' ? 'Yorum' : 'Reviews'],
                          ['price_avg_tl', language === 'tr' ? 'Ort. Fiyat' : 'Avg Price'],
                          ['price_min_tl', language === 'tr' ? 'Min Fiyat' : 'Min Price'],
                          ['price_max_tl', language === 'tr' ? 'Max Fiyat' : 'Max Price'],
                          ['discount_badges', language === 'tr' ? 'İndirim Rozeti' : 'Discount Badges'],
                        ].filter(([k]) => analysis.content_data.marketplace[k] != null).map(([key, label]) => {
                          const val = analysis.content_data.marketplace[key];
                          const suffix = key.startsWith('price_') ? ' TL' : '';
                          const isLongText = key === 'page_title';
                          return (
                            <div key={key} className={`bg-[#F4F4F5] p-3 ${isLongText ? 'col-span-2 md:col-span-4' : ''}`} data-testid={`mp-${key}`}>
                              <div className={`font-bold text-[#09090B] ${isLongText ? 'text-sm' : 'text-lg'}`}>
                                {isLongText ? String(val).slice(0, 120) : val}{suffix}
                              </div>
                              <div className="text-xs text-[#71717A] uppercase tracking-wide mt-1">{label}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Quick wins */}
                  {analysis.quick_wins && analysis.quick_wins.length > 0 && (
                    <div className="mb-6" data-testid="quick-wins-block">
                      <h3 className="font-semibold text-[#09090B] mb-3 flex items-center gap-2">
                        <Sparkle size={18} weight="fill" className="text-[#F59E0B]" />
                        {language === 'tr' ? 'Hızlı Kazanımlar (1 hafta)' : 'Quick Wins (1 week)'}
                      </h3>
                      <ul className="space-y-2">
                        {analysis.quick_wins.map((w, i) => (
                          <li key={i} className="flex items-start gap-2 text-[#09090B]">
                            <CheckCircle size={18} weight="fill" className="text-[#16A34A] flex-shrink-0 mt-0.5" />
                            <span>{w}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Issues */}
                  {analysis.issues && analysis.issues.length > 0 && (
                    <div className="mb-6" data-testid="issues-block">
                      <h3 className="font-semibold text-[#09090B] mb-3">
                        {language === 'tr' ? 'Sorunlar ve Öneriler' : 'Issues & Recommendations'} ({analysis.issues.length})
                      </h3>
                      <div className="space-y-3">
                        {analysis.issues.map((is, i) => (
                          <div key={i} className={`border-l-4 p-4 ${SEVERITY_COLOR[is.severity] || SEVERITY_COLOR.low}`} data-testid={`issue-${i}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <WarningCircle size={16} weight="fill" className={is.severity === 'high' ? 'text-[#DC2626]' : is.severity === 'medium' ? 'text-[#F59E0B]' : 'text-[#71717A]'} />
                              <span className="uppercase text-xs tracking-wide text-[#71717A]">{is.severity} · {is.category}</span>
                            </div>
                            <h4 className="font-semibold text-[#09090B]">{is.title}</h4>
                            <p className="text-[#52525B] text-sm mt-1">{is.description}</p>
                            {is.fix && (
                              <div className="mt-2 text-sm flex items-start gap-1">
                                <Wrench size={14} weight="bold" className="text-[#0F1B36] mt-1 flex-shrink-0" />
                                <span className="text-[#09090B]"><strong>{language === 'tr' ? 'Çözüm:' : 'Fix:'}</strong> {is.fix}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Roadmap */}
                  <div className="grid md:grid-cols-3 gap-4" data-testid="roadmap-block">
                    {[
                      { key: 'roadmap_30d', label: language === 'tr' ? '30 Gün' : '30 Days' },
                      { key: 'roadmap_60d', label: language === 'tr' ? '60 Gün' : '60 Days' },
                      { key: 'roadmap_90d', label: language === 'tr' ? '90 Gün' : '90 Days' },
                    ].map(col => (
                      <div key={col.key} className="border border-[#E4E4E7] p-4">
                        <h4 className="font-semibold text-[#09090B] mb-3">{col.label}</h4>
                        <ul className="space-y-2">
                          {(analysis[col.key] || []).map((item, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-[#52525B]">
                              <ArrowRight size={14} weight="bold" className="text-[#0F1B36] flex-shrink-0 mt-1" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoresPage;
